import { useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import {
  RiAddBoxLine,
  RiCloseLine,
  RiMore2Fill,
  RiShareForwardBoxLine,
} from '@remixicon/react';
import { BottomSheet } from '@/components/BottomSheet';
import {
  consumeInstallPrompt,
  hasInstallPrompt,
  subscribeInstallPrompt,
} from '@/pwa/installPrompt';
import { storage } from '@/services/storageService';

const MOBILE_QUERY = '(max-width: 1023px)';
const DISMISS_STORAGE_KEY = 'pwaInstallDismissedAt';
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000;

function isStandalone() {
  const modes = ['standalone', 'fullscreen', 'minimal-ui'];
  if (modes.some((mode) => window.matchMedia(`(display-mode: ${mode})`).matches)) {
    return true;
  }

  const nav = window.navigator as Navigator & { standalone?: boolean };
  return nav.standalone === true;
}

function isIosDevice() {
  const ua = window.navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
}

function isInAppBrowser() {
  return /FBAN|FBAV|Instagram|Line\/|Twitter|Snapchat/i.test(window.navigator.userAgent);
}

function isDismissedRecently() {
  try {
    const raw = storage.get<string>(DISMISS_STORAGE_KEY);
    if (!raw) return false;
    const dismissedAt = Number(raw);
    if (!Number.isFinite(dismissedAt)) return false;
    return Date.now() - dismissedAt < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

export function InstallAppPrompt() {
  const { pathname } = useLocation();
  const [isMobile, setIsMobile] = useState(() => window.matchMedia(MOBILE_QUERY).matches);
  const [installed, setInstalled] = useState(isStandalone);
  const [canPrompt, setCanPrompt] = useState(hasInstallPrompt);
  const [helpOpen, setHelpOpen] = useState(false);
  const [dismissed, setDismissed] = useState(isDismissedRecently);

  const hiddenRoute = pathname.startsWith('/pdf-render');
  const visible = isMobile && !installed && !dismissed && !hiddenRoute;
  const ios = isIosDevice();
  const inAppBrowser = isInAppBrowser();

  useEffect(() => {
    const media = window.matchMedia(MOBILE_QUERY);
    const sync = () => setIsMobile(media.matches);
    sync();
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    const onInstalled = () => setInstalled(true);
    const standaloneMedia = window.matchMedia('(display-mode: standalone)');
    const onMode = () => {
      if (isStandalone()) setInstalled(true);
    };

    window.addEventListener('appinstalled', onInstalled);
    standaloneMedia.addEventListener('change', onMode);
    return () => {
      window.removeEventListener('appinstalled', onInstalled);
      standaloneMedia.removeEventListener('change', onMode);
    };
  }, []);

  useEffect(() => subscribeInstallPrompt(() => setCanPrompt(hasInstallPrompt())), []);

  useEffect(() => {
    const root = document.documentElement;
    if (visible) {
      root.dataset.pwaInstall = 'true';
    } else {
      delete root.dataset.pwaInstall;
    }

    return () => {
      delete root.dataset.pwaInstall;
    };
  }, [visible]);

  function handleDismiss() {
    storage.set(DISMISS_STORAGE_KEY, String(Date.now()));
    setDismissed(true);
    setHelpOpen(false);
  }

  async function handleInstall() {
    if (!canPrompt) {
      setHelpOpen(true);
      return;
    }

    try {
      const accepted = await consumeInstallPrompt();
      if (accepted) setInstalled(true);
    } catch {
      setHelpOpen(true);
    }
  }

  if (!visible) return null;

  return (
    <>
      <div
        data-pwa-install-banner
        className="sticky top-0 z-[25] border-b border-emerald-800/30 bg-emerald-700 text-white shadow-md"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="flex h-12 items-center gap-2 px-2.5 sm:gap-3 sm:px-3">
          <img
            src="/apple-touch-icon.png"
            alt=""
            className="h-8 w-8 shrink-0 rounded-lg bg-white object-contain"
          />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold leading-tight">Instalar o OPP</p>
            <p className="truncate text-[11px] leading-tight text-emerald-100">
              Adicione o app à tela inicial
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              void handleInstall();
            }}
            className="shrink-0 rounded-full bg-white px-3 py-1.5 text-sm font-semibold text-emerald-800 active:scale-95"
          >
            Instalar
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Fechar"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-emerald-100 transition-colors hover:bg-emerald-800/50 hover:text-white active:scale-95"
          >
            <RiCloseLine className="h-5 w-5" />
          </button>
        </div>
      </div>

      <BottomSheet
        isOpen={helpOpen}
        onClose={() => setHelpOpen(false)}
        aria-labelledby="pwa-install-help-title"
      >
        <div className="px-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-2">
          <h2 id="pwa-install-help-title" className="text-base font-semibold text-slate-900">
            Como instalar
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            {inAppBrowser
              ? 'Abra este endereço no Safari ou no Chrome. O navegador interno do app não instala o OPP.'
              : ios
                ? 'No iPhone, a instalação é feita pelo menu de compartilhar do Safari.'
                : 'Se o instalador do navegador não abriu, use o menu do Chrome.'}
          </p>

          <ol className="mt-4 space-y-3">
            {ios ? (
              <>
                <InstallStep
                  icon={<RiShareForwardBoxLine className="h-5 w-5 text-emerald-700" />}
                  title="Toque em Compartilhar"
                  description="Na barra do Safari, o ícone é um quadrado com uma seta para cima."
                />
                <InstallStep
                  icon={<RiAddBoxLine className="h-5 w-5 text-emerald-700" />}
                  title="Adicionar à Tela de Início"
                  description="Role o menu e toque nessa opção, depois confirme em Adicionar."
                />
              </>
            ) : (
              <>
                <InstallStep
                  icon={<RiMore2Fill className="h-5 w-5 text-emerald-700" />}
                  title="Abra o menu do navegador"
                  description="Toque nos três pontos no canto da tela."
                />
                <InstallStep
                  icon={<RiAddBoxLine className="h-5 w-5 text-emerald-700" />}
                  title="Instalar app"
                  description='Escolha "Instalar app" ou "Adicionar à tela inicial" e confirme.'
                />
              </>
            )}
          </ol>
        </div>
      </BottomSheet>
    </>
  );
}

function InstallStep({
  icon,
  title,
  description,
}: {
  icon: ReactNode;
  title: string;
  description: string;
}) {
  return (
    <li className="flex items-start gap-3">
      <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-50">
        {icon}
      </span>
      <span>
        <span className="block text-sm font-semibold text-slate-800">{title}</span>
        <span className="mt-0.5 block text-sm text-slate-500">{description}</span>
      </span>
    </li>
  );
}
